// SVG string builders — avoid JSX expression wrappers so serialized output
// is clean, valid SVG that renders correctly through canvas.drawImage().

const escape = (s) =>
    String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

// Roman numeral helper — for the classy MMXXIV-style year on the sticker.
const toRoman = (num) => {
    const map = [
        [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
        [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
        [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
    ];
    let n = parseInt(num, 10) || 0;
    let out = "";
    for (const [v, s] of map) {
        while (n >= v) {
            out += s;
            n -= v;
        }
    }
    return out;
};

export const buildStickerSVG = ({
    color = "#a3e635",
    tierShort = "FOUNDER",
    tierCap = "FIRST 100 MEMBERS",
    memberNumber = "0047",
    joined = 2024,
}) => {
    // Rounded-square emblem echoing the EVIQ app icon:
    //   • Speedometer gauge (green → cyan gradient) at the top
    //   • "EVIQ" wordmark centered (EV white, IQ gradient)
    //   • Plug motif divider
    //   • Founder line etched subtly at the bottom (blends into the frame)
    const size = 600;
    const cx = size / 2;
    const gaugeCx = cx;
    const gaugeCy = 230;
    const rInner = 130;
    const rOuter = 180;

    // Build the tick dividers that split the gauge arc into 10 segments.
    // Angles measured from +x axis going clockwise. Arc spans 180°→360°
    // (i.e. the top half). 11 evenly-spaced radial lines create 10 segments.
    const ticks = [];
    for (let i = 0; i <= 10; i++) {
        const theta = Math.PI + (Math.PI * i) / 10; // π .. 2π
        const x1 = gaugeCx + rInner * Math.cos(theta);
        const y1 = gaugeCy + rInner * Math.sin(theta);
        const x2 = gaugeCx + rOuter * Math.cos(theta);
        const y2 = gaugeCy + rOuter * Math.sin(theta);
        ticks.push(
            `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="#050505" stroke-width="8"/>`
        );
    }

    // Needle rests roughly in the cyan zone (right-of-center on the gauge).
    const needleAngleDeg = 55; // 0° = straight up
    const romanYear = toRoman(joined);
    const memberEtched = `${escape(tierShort)} · #${escape(memberNumber)} · ${romanYear}`;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" role="img" aria-label="EVIQ ${escape(tierShort)} ${escape(memberNumber)} window sticker">
  <defs>
    <linearGradient id="eviq-brand" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#a3e635"/>
      <stop offset="55%" stop-color="#22d3ee"/>
      <stop offset="100%" stop-color="#06b6d4"/>
    </linearGradient>
    <linearGradient id="eviq-brand-soft" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#a3e635" stop-opacity="0.65"/>
      <stop offset="100%" stop-color="#06b6d4" stop-opacity="0.65"/>
    </linearGradient>
  </defs>

  <!-- Rounded-square tile background -->
  <rect x="30" y="30" width="${size - 60}" height="${size - 60}" rx="90" fill="#050505"/>
  <rect x="30" y="30" width="${size - 60}" height="${size - 60}" rx="90" fill="none" stroke="url(#eviq-brand-soft)" stroke-width="4"/>
  <rect x="52" y="52" width="${size - 104}" height="${size - 104}" rx="72" fill="none" stroke="#1a1a1c" stroke-width="1"/>

  <!-- Gauge arc (thick gradient stroke, capped) -->
  <path d="M ${gaugeCx - 155} ${gaugeCy} A 155 155 0 0 1 ${gaugeCx + 155} ${gaugeCy}" fill="none" stroke="url(#eviq-brand)" stroke-width="50"/>
  <!-- Tick dividers to give the gauge its segmented look -->
  ${ticks.join("\n  ")}

  <!-- Needle -->
  <g transform="translate(${gaugeCx} ${gaugeCy}) rotate(${needleAngleDeg})">
    <polygon points="0,-6 -160,0 0,6" fill="#ffffff"/>
    <circle cx="0" cy="0" r="10" fill="#ffffff"/>
    <circle cx="0" cy="0" r="4" fill="#050505"/>
  </g>

  <!-- EVIQ wordmark -->
  <text x="${cx}" y="380" text-anchor="middle" font-family="'Helvetica Neue', Arial, sans-serif" font-weight="900" font-size="110" letter-spacing="-4">
    <tspan fill="#ffffff">EV</tspan><tspan fill="url(#eviq-brand)">IQ</tspan>
  </text>

  <!-- Plug divider motif -->
  <g transform="translate(${cx} 425)">
    <line x1="-140" y1="0" x2="-30" y2="0" stroke="url(#eviq-brand-soft)" stroke-width="1.5"/>
    <line x1="30" y1="0" x2="140" y2="0" stroke="url(#eviq-brand-soft)" stroke-width="1.5"/>
    <g transform="translate(-14 -14)">
      <path d="M 4 4 h 6 v -8 h 8 v 8 h 6 v 10 a 10 10 0 0 1 -10 10 h -0 a 10 10 0 0 1 -10 -10 z" fill="url(#eviq-brand)"/>
      <line x1="14" y1="24" x2="14" y2="32" stroke="url(#eviq-brand)" stroke-width="2"/>
    </g>
  </g>

  <!-- Founder etched line — subtle, blends into the frame -->
  <text x="${cx}" y="490" text-anchor="middle" fill="url(#eviq-brand-soft)" font-family="'Helvetica Neue', Arial, sans-serif" font-weight="600" font-size="18" letter-spacing="7">${memberEtched}</text>
  <text x="${cx}" y="518" text-anchor="middle" fill="#3f3f46" font-family="'Helvetica Neue', Arial, sans-serif" font-weight="500" font-size="11" letter-spacing="5">${escape(tierCap)}</text>
</svg>`;
};

export const buildCertificateSVG = ({ user, color, tierLabel, tierCap, memberNumber }) => {
    const w = 850;
    const h = 1100;
    const joined = new Date(user.member_since).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" role="img" aria-label="EVIQ certificate">
  <rect width="${w}" height="${h}" fill="#050505"/>
  <rect x="30" y="30" width="${w - 60}" height="${h - 60}" fill="none" stroke="${color}" stroke-width="2"/>
  <rect x="46" y="46" width="${w - 92}" height="${h - 92}" fill="none" stroke="${color}" stroke-opacity="0.35" stroke-width="1"/>
  <text x="${w / 2}" y="140" text-anchor="middle" fill="${color}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="14" font-weight="700" letter-spacing="8">EVIQ EARTH INITIATIVE</text>
  <line x1="${w / 2 - 60}" y1="160" x2="${w / 2 + 60}" y2="160" stroke="${color}" stroke-width="1"/>
  <text x="${w / 2}" y="240" text-anchor="middle" fill="#ffffff" font-family="Georgia, serif" font-size="42" font-weight="700">Certificate of</text>
  <text x="${w / 2}" y="290" text-anchor="middle" fill="#ffffff" font-family="Georgia, serif" font-size="42" font-style="italic" font-weight="700">Founding Membership</text>
  <text x="${w / 2}" y="380" text-anchor="middle" fill="#a1a1aa" font-family="'Helvetica Neue', Arial, sans-serif" font-size="13" letter-spacing="6">AWARDED TO</text>
  <text x="${w / 2}" y="440" text-anchor="middle" fill="#ffffff" font-family="Georgia, serif" font-size="36" font-weight="600">${escape(user.display_name)}</text>
  <g transform="translate(${w / 2} 570)">
    <circle cx="0" cy="0" r="80" fill="#0a0a0a" stroke="${color}" stroke-width="3"/>
    <circle cx="0" cy="0" r="66" fill="none" stroke="${color}" stroke-opacity="0.4" stroke-width="1"/>
    <path d="M -12 -34 L 12 -34 L 3 -8 L 18 -8 L -10 34 L 0 8 L -14 8 Z" fill="${color}"/>
    <text x="0" y="60" text-anchor="middle" fill="${color}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="11" font-weight="700" letter-spacing="4">#${escape(memberNumber)}</text>
  </g>
  <text x="${w / 2}" y="700" text-anchor="middle" fill="#a1a1aa" font-family="'Helvetica Neue', Arial, sans-serif" font-size="12" letter-spacing="6">RECOGNIZED AS</text>
  <text x="${w / 2}" y="740" text-anchor="middle" fill="${color}" font-family="Georgia, serif" font-style="italic" font-size="32" font-weight="600">${escape(tierLabel)}</text>
  <text x="${w / 2}" y="770" text-anchor="middle" fill="#52525b" font-family="'Helvetica Neue', Arial, sans-serif" font-size="12" letter-spacing="4">${escape(tierCap.toUpperCase())}</text>
  <text x="${w / 2}" y="850" text-anchor="middle" fill="#a1a1aa" font-family="Georgia, serif" font-style="italic" font-size="15">"For membership that helps restore forests, remove ocean plastic,</text>
  <text x="${w / 2}" y="874" text-anchor="middle" fill="#a1a1aa" font-family="Georgia, serif" font-style="italic" font-size="15">recycle EV batteries, and support veterans building the clean-energy future."</text>
  <line x1="200" y1="980" x2="${w - 200}" y2="980" stroke="#27272a"/>
  <text x="200" y="1010" fill="#a1a1aa" font-family="'Helvetica Neue', Arial, sans-serif" font-size="11" letter-spacing="3">MEMBER SINCE</text>
  <text x="200" y="1034" fill="#ffffff" font-family="'Helvetica Neue', Arial, sans-serif" font-size="15" font-weight="600">${escape(joined)}</text>
  <text x="${w - 200}" y="1010" text-anchor="end" fill="#a1a1aa" font-family="'Helvetica Neue', Arial, sans-serif" font-size="11" letter-spacing="3">CERTIFICATE ID</text>
  <text x="${w - 200}" y="1034" text-anchor="end" fill="#ffffff" font-family="'Menlo', 'Courier New', monospace" font-size="15" font-weight="600">EVIQ-${escape(memberNumber)}</text>
</svg>`;
};
